import { useEffect, useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Loader2, ArrowRight } from "lucide-react";
import { useRazorpayPayment } from "@/hooks/useRazorpayPayment";
import { usePaymentProvider } from "@/hooks/usePaymentProvider";
import { usePayPalPayment } from "@/hooks/usePayPalPayment";
import { PayPalButton } from "@/components/PayPalButton";
import { useIsMobile } from "@/hooks/use-mobile";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: string;
  storiesUsed: number;
  limit: number;
  isExpired?: boolean;
  downloadAttempt?: boolean;
  onPaymentSuccess?: () => void;
}

// Pricing differs based on payment provider/country
const getPlans = (isIndianUser: boolean) => [
  {
    name: "Basic",
    value: "basic" as const,
    limit: isIndianUser ? 20 : 50,
    limitType: "monthly",
    price: isIndianUser ? "₹149/mo" : "$3.99/mo",
    features: [
      `${isIndianUser ? "20" : "50"} stories/month`,
      "Audio narration"
    ],
    missingFeatures: ["Downloads", "Story and character personalization"],
  },
  {
    name: "Premium",
    value: "premium" as const,
    limit: isIndianUser ? 50 : 100,
    limitType: "monthly",
    price: isIndianUser ? "₹279/mo" : "$5.99/mo",
    features: [
      `${isIndianUser ? "50" : "100"} stories/month`,
      "Audio narration",
      "Unlimited downloads",
      "Story and character personalization"
    ],
    popular: true,
  },
];

export function UpgradeDialog({
  open,
  onOpenChange,
  currentPlan,
  storiesUsed,
  limit,
  isExpired = false,
  downloadAttempt = false,
  onPaymentSuccess,
}: UpgradeDialogProps) {
  const { initiatePayment, isLoading, reviseSubscription: reviseRazorpay, isRevising: isRevisingRazorpay } = useRazorpayPayment();
  const { provider, isLoading: providerLoading } = usePaymentProvider();
  const { reviseSubscription: revisePayPal, isRevising: isRevisingPayPal } = usePayPalPayment();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  
  // Track existing subscription for revise flow
  const [existingSubscriptionId, setExistingSubscriptionId] = useState<string | null>(null);
  const [existingPaymentProvider, setExistingPaymentProvider] = useState<string | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [isProcessingPlanChange, setIsProcessingPlanChange] = useState(false);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);


  // Cleanup polling on unmount or dialog close
  useEffect(() => {
    if (!open && pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
      setIsProcessingPlanChange(false);
      setProcessingPlan(null);
    }
  }, [open]);

  // Fetch existing subscription when dialog opens
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!open) return;
      
      setIsLoadingSubscription(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: subscription } = await supabase
          .from("subscriptions")
          .select("provider_subscription_id, payment_provider, plan")
          .eq("user_id", user.id)
          .maybeSingle();

        if (subscription?.provider_subscription_id) {
          setExistingSubscriptionId(subscription.provider_subscription_id);
          setExistingPaymentProvider(subscription.payment_provider);
        } else {
          setExistingSubscriptionId(null);
          setExistingPaymentProvider(null);
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchSubscription();
  }, [open]);

  const handleSubscribe = (planValue: "basic" | "premium") => {
    initiatePayment({
      plan: planValue,
      onSuccess: () => {
        onOpenChange(false);
        onPaymentSuccess?.();
      },
    });
  };

  const handlePayPalSuccess = () => {
    onOpenChange(false);
    onPaymentSuccess?.();
  };

  // Poll for plan change after immediate upgrade
  const pollForPlanChange = useCallback(async (
    targetPlan: string,
    attempt: number = 1,
    maxAttempts: number = 15
  ): Promise<boolean> => {
    if (attempt > maxAttempts) {
      return false;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data: subscription } = await supabase
        .from("subscriptions")
        .select("plan, pending_plan")
        .eq("user_id", user.id)
        .maybeSingle();

      // For upgrades, check if plan has changed
      if (subscription?.plan === targetPlan) {
        return true;
      }

      // Wait with exponential backoff (2s, 2s, 2s, 4s, 4s, 8s...)
      const delay = attempt <= 3 ? 2000 : attempt <= 5 ? 4000 : 8000;
      
      return new Promise((resolve) => {
        pollingRef.current = setTimeout(async () => {
          const result = await pollForPlanChange(targetPlan, attempt + 1, maxAttempts);
          resolve(result);
        }, delay);
      });
    } catch (error) {
      console.error("Error polling for plan change:", error);
      return false;
    }
  }, []);

  // Handle plan change via PayPal Revise API
  const handlePayPalPlanChange = async (newPlan: "basic" | "premium") => {
    if (!existingSubscriptionId) return;
    
    const result = await revisePayPal(existingSubscriptionId, newPlan);
    
    if (result.success) {
      if (result.immediate) {
        if (result.is_downgrade) {
          // Downgrade is scheduled for end of billing cycle, close immediately
          onOpenChange(false);
          onPaymentSuccess?.();
        } else {
          // Upgrade: poll for the webhook to update the database
          setIsProcessingPlanChange(true);
          setProcessingPlan(newPlan);
          
          const planUpdated = await pollForPlanChange(newPlan);
          
          setIsProcessingPlanChange(false);
          setProcessingPlan(null);
          
          if (planUpdated) {
            toast({
              title: "Upgrade Complete!",
              description: `You're now on the ${newPlan === "premium" ? "Premium" : "Basic"} plan.`,
            });
            onOpenChange(false);
            onPaymentSuccess?.();
          } else {
            toast({
              title: "Upgrade Processing",
              description: "Your upgrade is taking longer than expected. Please refresh the page in a moment.",
            });
            onOpenChange(false);
            onPaymentSuccess?.();
          }
        }
      } else if (result.approval_url) {
        // Redirect to PayPal for approval
        window.location.href = result.approval_url;
      }
    }
  };

  // Handle plan change via Razorpay Revise API
  const handleRazorpayPlanChange = async (newPlan: "basic" | "premium") => {
    setIsProcessingPlanChange(true);
    setProcessingPlan(newPlan);

    try {
      await reviseRazorpay({
        newPlan,
        onSuccess: async () => {
          // For upgrades, poll for the change to be reflected
          const isUpgrade = currentPlan === "basic" && newPlan === "premium";
          
          if (isUpgrade) {
            const planUpdated = await pollForPlanChange(newPlan);
            
            if (planUpdated) {
              toast({
                title: "Upgrade Complete!",
                description: `You're now on the ${newPlan === "premium" ? "Premium" : "Basic"} plan.`,
              });
            } else {
              toast({
                title: "Upgrade Processing",
                description: "Your upgrade is taking longer than expected. Please refresh the page in a moment.",
              });
            }
          } else {
            // Downgrade scheduled
            toast({
              title: "Plan Change Scheduled",
              description: `Your plan will change to ${newPlan === "basic" ? "Basic" : "Premium"} at the end of your billing cycle.`,
            });
          }
          
          onOpenChange(false);
          onPaymentSuccess?.();
        },
        onError: (error) => {
          toast({
            title: "Plan Change Failed",
            description: error || "Failed to change plan. Please try again.",
            variant: "destructive",
          });
        },
      });
    } finally {
      setIsProcessingPlanChange(false);
      setProcessingPlan(null);
    }
  };

  const title = downloadAttempt
    ? "Upgrade to Download Stories"
    : isExpired 
      ? "Your plan has expired" 
      : currentPlan === "basic"
        ? "Upgrade to Premium"
        : "You've reached your story limit";

  const description = downloadAttempt ? (
    "Downloading stories is a Premium feature. Upgrade now to download your stories!"
  ) : isExpired ? (
    "Your paid subscription has expired. Renew now to continue creating more stories!"
  ) : currentPlan === "basic" ? (
    <>
      Upgrade to Premium for 100 stories/month + personalization features!
      <span className="block text-primary text-sm mt-1">
        ✨ Unused Basic stories will carry over to your first Premium month!
      </span>
    </>
  ) : currentPlan === "free" ? (
    <>
      Your free trial has ended. You've used {storiesUsed} of {limit} stories.
      Subscribe to create more stories!
    </>
  ) : (
    <>
      You've used {storiesUsed} of {limit} stories this month.
      Upgrade to create more stories!
    </>
  );

  // Determine if this is an existing subscriber changing plans
  const canChangePlan = existingSubscriptionId && !isExpired && currentPlan !== "free";
  const isRazorpayPlanChange = provider === "razorpay" && existingPaymentProvider === "razorpay" && canChangePlan;
  const isPayPalPlanChange = provider === "paypal" && existingPaymentProvider === "paypal" && canChangePlan;
  const isRevising = isRevisingPayPal || isRevisingRazorpay;
  
  // Get plans based on user's payment provider (Indian users use Razorpay)
  const isIndianUser = provider === "razorpay";
  const plans = getPlans(isIndianUser);

  const content = (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        {providerLoading || isLoadingSubscription ? (
          <>
            <Skeleton className="h-48 w-full rounded-lg" />
            <Skeleton className="h-48 w-full rounded-lg" />
          </>
        ) : (
          plans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.value && !isExpired;
            const canChangeToPlan = !isCurrentPlan;
            const isUpgrade = currentPlan === "basic" && plan.value === "premium";
            const isDowngrade = currentPlan === "premium" && plan.value === "basic";

            return (
              <Card
                key={plan.value}
                className={`p-3 relative ${
                  isCurrentPlan
                    ? "border-primary bg-primary/5"
                    : plan.popular
                    ? "border-primary/50"
                    : ""
                }`}
              >
                {plan.popular && !isCurrentPlan && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary">
                    Popular
                  </Badge>
                )}
                {isCurrentPlan && (
                  <Badge variant="outline" className="absolute -top-2 left-1/2 -translate-x-1/2">
                    Current
                  </Badge>
                )}
                <div className="flex items-center justify-between mb-3 mt-2">
                  <div>
                    <h3 className="font-semibold text-lg">{plan.name}</h3>
                    <p className="text-xl font-bold text-primary">{plan.price}</p>
                  </div>
                {/* Razorpay: New subscription button */}
                  {provider === "razorpay" && canChangeToPlan && !isRazorpayPlanChange && (
                    <Button 
                      className="h-10" 
                      variant={plan.popular ? "default" : "outline"}
                      onClick={() => handleSubscribe(plan.value)}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      {currentPlan === "free" ? "Subscribe" : isExpired ? "Renew" : "Subscribe"}
                    </Button>
                  )}
                  {/* Razorpay: Plan change button for existing subscribers */}
                  {isRazorpayPlanChange && canChangeToPlan && (
                    <Button 
                      className="h-10" 
                      variant={plan.popular ? "default" : "outline"}
                      onClick={() => handleRazorpayPlanChange(plan.value)}
                      disabled={isRevising || isProcessingPlanChange}
                    >
                      {(isRevising || (isProcessingPlanChange && processingPlan === plan.value)) ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      {isProcessingPlanChange && processingPlan === plan.value 
                        ? "Processing..." 
                        : isUpgrade ? "Upgrade" : "Downgrade"}
                    </Button>
                  )}
                  {/* PayPal: Plan change button for existing subscribers */}
                  {isPayPalPlanChange && canChangeToPlan && (
                    <Button 
                      className="h-10" 
                      variant={plan.popular ? "default" : "outline"}
                      onClick={() => handlePayPalPlanChange(plan.value)}
                      disabled={isRevising || isProcessingPlanChange}
                    >
                      {(isRevising || (isProcessingPlanChange && processingPlan === plan.value)) ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      {isProcessingPlanChange && processingPlan === plan.value 
                        ? "Processing..." 
                        : isUpgrade ? "Upgrade" : "Downgrade"}
                    </Button>
                  )}
                </div>
                {isDowngrade && (isPayPalPlanChange || isRazorpayPlanChange) && (
                  <p className="text-xs text-muted-foreground mb-3">
                    Downgrade will take effect at the end of your current billing cycle
                  </p>
                )}
                <ul className="space-y-1 mb-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                  {plan.missingFeatures?.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="w-4 h-4 text-center flex-shrink-0">✕</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                {/* PayPal button for NEW subscribers only */}
                {provider === "paypal" && !isPayPalPlanChange && !isRazorpayPlanChange && canChangeToPlan && (
                  <PayPalButton plan={plan.value} onSuccess={handlePayPalSuccess} />
                )}
              </Card>
            );
          })
        )}
      </div>

      <div className="flex justify-center mt-2">
        <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
          Maybe Later
        </Button>
      </div>
    </>
  );

  // Early-bird users should never see this dialog
  if (currentPlan === 'early-bird') {
    return null;
  }

  // Use Sheet on mobile for better UX
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[85vh] overflow-y-auto rounded-t-2xl">
          <SheetHeader className="text-center">
            <SheetTitle className="text-xl font-semibold">
              {title}
            </SheetTitle>
            <SheetDescription className="text-center">
              {description}
            </SheetDescription>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">
            {title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {description}
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
